import React from "react";
import { useTranslation } from "react-i18next";
import UserSettingsService, { SingleWorkspaceSettingsPaths } from "src/services/userSettingsService";

import { Box, BoxProps, Collapse, IconButton, Typography, useTheme } from "@mui/material";

interface CollapsibleGroupContextType {
    expandAll: boolean | null;
    setExpandAll: React.Dispatch<React.SetStateAction<boolean | null>>;
}
export const CollapsibleGroupContext = React.createContext<CollapsibleGroupContextType>({
    expandAll: null,
    setExpandAll: () => null,
});
type CollapsibleGroupProps = React.PropsWithChildren<{
    initialValue?: boolean | null;
}>;
export const CollapsibleGroupProvider = (props: CollapsibleGroupProps) => {
    const [expandAll, setExpandAll] = React.useState<boolean | null>(props.initialValue || null);

    return (
        <CollapsibleGroupContext.Provider value={{ expandAll, setExpandAll }}>{props.children}</CollapsibleGroupContext.Provider>
    );
};

type CollapsibleContextType = {
    open: boolean;
    disabled: boolean;
    setOpen: (open: boolean) => void;
};
const CollapsibleContext = React.createContext<CollapsibleContextType>({
    open: false,
    disabled: false,
    setOpen: () => null,
});
type CollapsibleProviderType = React.PropsWithChildren<{
    initialValue?: boolean;
    onChange?: (open: boolean) => void;
    disabled?: boolean;
}>;
export const CollapsibleProvider = ({ onChange, ...props }: CollapsibleProviderType) => {
    const [open, setOpen] = React.useState(!!props.initialValue);
    const { expandAll, setExpandAll } = React.useContext(CollapsibleGroupContext);

    const onOpen = (value: boolean) => {
        setOpen(value);
        if (onChange) {
            onChange(value);
            setExpandAll(null);
        }
    };

    // Depends on the callback rather than the whole props object, which is a new object on every redraw. With
    // "expand all" active that re-ran the effect constantly, and each run wrote the section state to storage -
    // on the provisioning page, a burst of writes per keystroke across every section.
    React.useEffect(() => {
        if (expandAll === null) {
            return;
        }

        setOpen(expandAll);
        onChange?.(expandAll);
    }, [expandAll, onChange]);

    return (
        <CollapsibleContext.Provider value={{ open, setOpen: onOpen, disabled: props.disabled || false }}>
            <Box>{props.children}</Box>
        </CollapsibleContext.Provider>
    );
};

type CollapsibleSectionSummaryProps = React.PropsWithChildren<BoxProps> & {
    action?: React.ReactNode;
};
export const CollapsibleSectionSummary = ({ children, action, style, ...props }: CollapsibleSectionSummaryProps) => {
    const { t } = useTranslation(["common"]);
    const context = React.useContext(CollapsibleContext);
    const theme = useTheme();

    if (!context) {
        throw new Error(t("common:error.collapsibleWrapper.noContext"));
    }

    return (
        <Box display="flex" alignItems="center" style={{ marginBottom: theme.spacing(1), ...style }} {...props}>
            <Box display="flex" alignItems="center" style={{ cursor: "pointer" }} onClick={() => context.setOpen(!context.open)}>
                <IconButton size="large">
                    {context.open ? (
                        <i className="fa-solid fa-chevron-down" style={{ fontSize: 16 }} />
                    ) : (
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: 16 }} />
                    )}
                </IconButton>
                {children}
            </Box>
            {action ? <Box ml="auto">{action}</Box> : null}
        </Box>
    );
};

export const CollapsibleSectionDetails = (
    props: React.PropsWithChildren<{
        error?: string;
    }>,
) => {
    const context = React.useContext(CollapsibleContext);
    const theme = useTheme();
    const { t } = useTranslation(["common"]);

    if (!context) {
        throw new Error(t("common:error.collapsibleWrapper.useInsideContext"));
    }

    return (
        <Collapse in={context.open} collapsedSize={0}>
            {props.error ? (
                <Typography color="error" variant="body2">
                    {props.error}
                </Typography>
            ) : null}
            <Box
                style={{
                    padding: 0,
                    paddingTop: 0,
                    pointerEvents: context.disabled ? "none" : "auto",
                    opacity: context.disabled ? 0.5 : 1,
                    paddingBottom: theme.spacing(4),
                }}
            >
                {props.children}
            </Box>
        </Collapse>
    );
};

type CollapsibleSectionTitleProps = {
    error?: string;
    size?: "small" | "medium" | "large";
    bold?: boolean;
};
export const CollapsibleSectionTitle = ({
    size = "large",
    bold = false,
    ...props
}: React.PropsWithChildren<CollapsibleSectionTitleProps>) => {
    const theme = useTheme();

    const getVariant = () => {
        switch (size) {
            case "small":
                return "body2";
            case "medium":
                return "subtitle1";
            case "large":
                return "h6";
        }
    };

    return (
        <Box
            style={{
                display: "flex",
                alignItems: "flex-end",
                gap: theme.spacing(2),
            }}
        >
            <Typography variant={getVariant()} style={{ fontWeight: bold ? 600 : 400 }}>
                {props.children}
            </Typography>
            {props.error ? (
                <Typography variant="caption" color="error">
                    {props.error}
                </Typography>
            ) : null}
        </Box>
    );
};

export const useCollapsiblePersistence = (storageKey: SingleWorkspaceSettingsPaths, sectionkey: string) => {
    const localStorageValue = () => UserSettingsService.getProvisioningPageCollapsedSections();
    const onChange = (open: boolean) => {
        const collapsedSection = localStorageValue();
        collapsedSection[sectionkey] = open;
        UserSettingsService.setWorkspaceValue(storageKey, collapsedSection);
    };
    return { value: localStorageValue()[sectionkey], onChange };
};
